import { CheckOutlined, LockOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateLeadSubmissionPaymentLink,
  getBusinessTestimonials,
  getLeadSubmissionCollabs,
} from "services/SauceService";
import PartnerPaywallContent from "components/paywall/PartnerPaywallContent";
import { getPartnerExperience } from "partners/partnerExperiences";
import { BusinessTestimonialResponseItem } from "interfaces/business-testimonial";
import {
  GenerateLeadSubmissionPaymentLinkPayload,
  LeadSubmissionCollab,
} from "interfaces/lead-submission";
import { getSelfCampaignQueryParams } from "./queryParams";
import {
  getSelfServeCampaignCachedBusinessTestimonials,
  getSelfServeCampaignCachedCollabs,
  getSelfServeCampaignDraft,
  saveSelfServeCampaignCachedBusinessTestimonials,
  saveSelfServeCampaignCachedCollabs,
  saveSelfServeCampaignActivateStepDraft,
  saveSelfServeCampaignMeta,
} from "./storage";
import {
  SelfServeCampaignActivatePlanId,
  SelfServeCampaignBusinessType,
  SelfServeCampaignPlatform,
} from "./types";
import SelfCampaignPageLayout from "./components/SelfCampaignPageLayout";
import SelfCampaignProgress from "./components/SelfCampaignProgress";
import "./SelfCampaignActivate.scss";

type ActivatePlan = {
  id: SelfServeCampaignActivatePlanId;
  name: string;
  priceLabel: string;
  ctaLabel: string;
  features: string[];
  isMostPopular?: boolean;
};

type CreatorExample = {
  handle: string;
  metric: string;
  image: string;
};

type Testimonial = {
  id: string;
  name: string;
  roleDescription: string;
  quote: string;
  logoUrl?: string;
  accent: "sunset" | "warm";
};

const ACTIVATE_PLANS: ActivatePlan[] = [
  {
    id: "talk_of_town_299",
    name: "Talk Of The Town Plan",
    priceLabel: "$299",
    ctaLabel: "Launch & Invite Creators — $299/mo",
    features: [
      "3 interested local influencers",
      "Unlimited locations",
      "3 posts + 3 stories",
      "10K+ local followers reach",
    ],
  },
  {
    id: "campaign_champagne_499",
    name: "Campaign Champagne Plan",
    priceLabel: "$499",
    ctaLabel: "Launch & Invite Creators — $499/mo",
    features: [
      "5 interested local influencers",
      "Unlimited locations",
      "5 posts + 5 stories",
      "16K+ local followers reach",
      "Priority influencer access",
    ],
    isMostPopular: true,
  },
  {
    id: "high_impact_999",
    name: "High Impact Plan",
    priceLabel: "$999",
    ctaLabel: "Launch & Invite Creators — $999/mo",
    features: [
      "10 interested local influencers",
      "Unlimited locations",
      "10 posts + 10 stories",
      "33K+ local followers reach",
      "Dedicated strategist",
      "Competitive insights",
      "Priority influencer access",
    ],
  },
];

const CREATOR_EXAMPLES_FALLBACK: CreatorExample[] = [
  {
    handle: "@foodie_la",
    metric: "1.2K likes",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/6b514a6700-41ce1d2c06e364f860d5.png",
  },
  {
    handle: "@la_eats",
    metric: "14K views",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/803366df5e-8c573f556748a0c75b48.png",
  },
  {
    handle: "@tacotime",
    metric: "3.5K views",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/8edd9ccf91-af13e375a929c071ae12.png",
  },
];

const TESTIMONIALS_FALLBACK: Testimonial[] = [
  {
    id: "fallback-1",
    name: "Taco District",
    roleDescription: "Silver Lake, Los Angeles",
    quote:
      '"18 creators applied in the first week. The content brought new customers that same weekend."',
    accent: "sunset",
  },
  {
    id: "fallback-2",
    name: "Little Tokyo Ramen",
    roleDescription: "Los Angeles",
    quote:
      '"Campaign setup took less than 20 minutes and creators started applying the same day."',
    accent: "warm",
  },
];

const ALL_PLANS_INCLUDE = [
  "Invite creators and receive guaranteed applications",
  "AI creator matching or manual selection",
  "Flexible compensation options (food, cash, affiliate)",
  "Direct chat with creators",
  "Cancel anytime before applications arrive",
];

const BUSINESS_TYPE_LABELS: Record<SelfServeCampaignBusinessType, string> = {
  restaurant_food_beverage: "Restaurant",
  cafe_coffee_shop: "Cafe",
  bar_nightlife: "Bar / Nightlife",
  hotel_resort: "Hotel / Resort",
  travel_tourism: "Travel / Tourism",
  event_venue: "Event Venue",
  beauty_skincare: "Beauty / Skincare",
  fitness_wellness: "Fitness / Wellness",
  fashion_apparel: "Fashion / Apparel",
  retail_local_shop: "Retail",
  consumer_brand: "Consumer Brand",
  e_commerce: "E-commerce",
  entertainment_attractions: "Entertainment",
  arts_culture: "Arts / Culture",
  other: "Local Business",
};

const BUSINESS_TYPE_EMOJIS: Record<SelfServeCampaignBusinessType, string> = {
  restaurant_food_beverage: "🍽️",
  cafe_coffee_shop: "☕",
  bar_nightlife: "🍸",
  hotel_resort: "🏨",
  travel_tourism: "🌍",
  event_venue: "🎉",
  beauty_skincare: "💄",
  fitness_wellness: "🧘",
  fashion_apparel: "👗",
  retail_local_shop: "🛍️",
  consumer_brand: "📦",
  e_commerce: "💻",
  entertainment_attractions: "🎢",
  arts_culture: "🎨",
  other: "🏪",
};

const PLATFORM_LABELS: Record<SelfServeCampaignPlatform, string> = {
  instagram: "Instagram Post / Reel / Story",
  instagram_tiktok: "Instagram + TikTok Post / Reel / Story",
};

const DEFAULT_CREATORS_AMOUNT = 493;
const BUSINESS_TESTIMONIALS_MAX_ATTEMPTS = 3;
const EMPTY_TESTIMONIALS_RETRY_DELAY_MS = 400;
const SELF_SERVICE_WELCOME_REDIRECT_URL = "https://www.mustard.love/welcome";

const PLAN_NAME_BY_ID: Record<
  SelfServeCampaignActivatePlanId,
  GenerateLeadSubmissionPaymentLinkPayload["planName"]
> = {
  talk_of_town_299: "talk-town",
  campaign_champagne_499: "champagne",
  high_impact_999: "high-impact",
};

const PLAN_ID_BY_NAME: Record<
  GenerateLeadSubmissionPaymentLinkPayload["planName"],
  SelfServeCampaignActivatePlanId
> = {
  "talk-town": "talk_of_town_299",
  champagne: "campaign_champagne_499",
  "high-impact": "high_impact_999",
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
};

const asTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const extractPaymentLinkUrl = (payload: unknown): string | undefined => {
  const payloadRecord = asRecord(payload);
  const nestedPayload = asRecord(payloadRecord?.data);
  const candidates = [
    payloadRecord?.url,
    payloadRecord?.paymentLink,
    payloadRecord?.paymentLinkUrl,
    payloadRecord?.checkoutUrl,
    payloadRecord?.link,
    nestedPayload?.url,
    nestedPayload?.paymentLink,
    nestedPayload?.paymentLinkUrl,
    nestedPayload?.checkoutUrl,
    nestedPayload?.link,
  ];

  for (const candidate of candidates) {
    const url = asTrimmedString(candidate);
    if (url) {
      return url;
    }
  }

  return undefined;
};

const resolveLocationLabel = (storeAddress?: string): string => {
  const normalized = (storeAddress ?? "").trim();
  if (!normalized) {
    return "Los Angeles, CA";
  }

  const parts = normalized
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    const city = parts[parts.length - 3];
    const stateCode = parts[parts.length - 2].split(" ")[0];
    return `${city}, ${stateCode}`;
  }

  if (parts.length >= 2) {
    return `${parts[0]}, ${parts[1]}`;
  }

  return parts[0];
};

const getTestimonialAccent = (index: number): "sunset" | "warm" => {
  return index % 2 === 0 ? "sunset" : "warm";
};

const mapTestimonialsToUi = (
  items: BusinessTestimonialResponseItem[],
): Testimonial[] => {
  return items.map((item, index) => ({
    id: item._id || `${item.name}-${index}`,
    name: item.name,
    roleDescription: item.roleDescription,
    quote: item.dscription,
    logoUrl: item.logoUrl,
    accent: getTestimonialAccent(index),
  }));
};

const formatCompactNumber = (value: number): string => {
  if (!Number.isFinite(value) || value < 0) {
    return "0";
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(".0", "")}M`;
  }

  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1).replace(".0", "")}K`;
  }

  return `${value}`;
};

const normalizeCreatorHandle = (value: string): string => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "@creator";
  }

  if (trimmed.startsWith("@")) {
    return trimmed;
  }

  if (/\s/.test(trimmed)) {
    return trimmed;
  }

  return `@${trimmed}`;
};

const mapCollabsToCreatorExamples = (
  collabs: LeadSubmissionCollab[],
): CreatorExample[] => {
  return collabs.slice(0, 3).map((collab) => ({
    handle: normalizeCreatorHandle(collab.creatorUsername),
    metric: `${formatCompactNumber(collab.followers)} followers`,
    image: collab.postCoverImg,
  }));
};

const wait = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

const getBusinessTestimonialsWithRetry = async (): Promise<
  BusinessTestimonialResponseItem[]
> => {
  for (
    let attempt = 0;
    attempt < BUSINESS_TESTIMONIALS_MAX_ATTEMPTS;
    attempt += 1
  ) {
    const response = await getBusinessTestimonials();

    if (response.length > 0) {
      return response;
    }

    const isLastAttempt = attempt === BUSINESS_TESTIMONIALS_MAX_ATTEMPTS - 1;
    if (!isLastAttempt) {
      await wait(EMPTY_TESTIMONIALS_RETRY_DELAY_MS);
    }
  }

  return [];
};

const SelfCampaignActivate: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(
    () => getSelfCampaignQueryParams(location.search),
    [location.search],
  );
  const draft = useMemo(() => getSelfServeCampaignDraft(), []);
  const resolvedPartner = useMemo(
    () => queryParams.partner || draft?.meta?.partner,
    [draft?.meta?.partner, queryParams.partner],
  );
  const partnerExperience = useMemo(
    () => getPartnerExperience(resolvedPartner),
    [resolvedPartner],
  );
  const shouldShowPartnerPaywall =
    partnerExperience?.activateSurface === "partner_paywall";
  const cachedTestimonials = useMemo(
    () => getSelfServeCampaignCachedBusinessTestimonials(),
    [],
  );
  const leadSubmissionId = draft?.meta?.leadSubmissionId;
  const storeAddress = draft?.createStep?.storeAddress;
  const cachedCollabs = useMemo(() => {
    if (!leadSubmissionId) {
      return null;
    }

    return getSelfServeCampaignCachedCollabs(leadSubmissionId, storeAddress);
  }, [leadSubmissionId, storeAddress]);

  const [launchingPlanId, setLaunchingPlanId] =
    useState<SelfServeCampaignActivatePlanId | null>(null);
  const [launchingPartnerPaywall, setLaunchingPartnerPaywall] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [testimonials, setTestimonials] = useState<Testimonial[]>(() => {
    if (!cachedTestimonials) {
      return [];
    }

    return mapTestimonialsToUi(cachedTestimonials);
  });
  const [loadingTestimonials, setLoadingTestimonials] =
    useState(!cachedTestimonials);
  const [creatorsAmount, setCreatorsAmount] = useState<number>(
    cachedCollabs?.creatorsAmount && cachedCollabs.creatorsAmount > 0
      ? cachedCollabs.creatorsAmount
      : DEFAULT_CREATORS_AMOUNT,
  );
  const [creatorExamples, setCreatorExamples] = useState<CreatorExample[]>(
    () => {
      if (!cachedCollabs || cachedCollabs.topCollabs.length === 0) {
        return [];
      }

      return mapCollabsToCreatorExamples(cachedCollabs.topCollabs);
    },
  );
  const [loadingCreatorExamples, setLoadingCreatorExamples] = useState(
    !cachedCollabs || cachedCollabs.topCollabs.length === 0,
  );

  useEffect(() => {
    if (leadSubmissionId) {
      return;
    }

    navigate(
      {
        pathname: "/self-campaign",
        search: location.search,
      },
      { replace: true },
    );
  }, [leadSubmissionId, location.search, navigate]);

  useEffect(() => {
    if (!queryParams.partner) {
      return;
    }

    saveSelfServeCampaignMeta({ partner: queryParams.partner });
  }, [queryParams.partner]);

  useEffect(() => {
    let isMounted = true;

    const loadBusinessTestimonials = async () => {
      if (cachedTestimonials) {
        return;
      }

      try {
        setLoadingTestimonials(true);
        const response = await getBusinessTestimonialsWithRetry();

        if (!isMounted) {
          return;
        }

        if (response.length > 0) {
          setTestimonials(mapTestimonialsToUi(response));
          saveSelfServeCampaignCachedBusinessTestimonials(response);
          return;
        }

        setTestimonials(TESTIMONIALS_FALLBACK);
      } catch (_error) {
        if (isMounted) {
          setTestimonials(TESTIMONIALS_FALLBACK);
        }
      } finally {
        if (isMounted) {
          setLoadingTestimonials(false);
        }
      }
    };

    void loadBusinessTestimonials();

    return () => {
      isMounted = false;
    };
  }, [cachedTestimonials]);

  useEffect(() => {
    let isMounted = true;

    const resolveCreatorsAmount = async () => {
      if (cachedCollabs?.creatorsAmount && cachedCollabs.creatorsAmount > 0) {
        setCreatorsAmount(cachedCollabs.creatorsAmount);
      }

      if (cachedCollabs && cachedCollabs.topCollabs.length > 0) {
        setCreatorExamples(
          mapCollabsToCreatorExamples(cachedCollabs.topCollabs),
        );
        setLoadingCreatorExamples(false);
        return;
      }

      if (!leadSubmissionId) {
        setLoadingCreatorExamples(false);
        return;
      }

      try {
        setLoadingCreatorExamples(true);
        const response = await getLeadSubmissionCollabs(leadSubmissionId);

        if (!isMounted) {
          return;
        }

        const nextCreatorsAmount =
          response.creatorsAmount > 0
            ? response.creatorsAmount
            : DEFAULT_CREATORS_AMOUNT;

        setCreatorsAmount(nextCreatorsAmount);
        setCreatorExamples(
          response.topCollabs.length > 0
            ? mapCollabsToCreatorExamples(response.topCollabs)
            : CREATOR_EXAMPLES_FALLBACK,
        );
        saveSelfServeCampaignCachedCollabs(
          leadSubmissionId,
          response,
          storeAddress,
        );
      } catch (_error) {
        if (isMounted) {
          setCreatorsAmount(DEFAULT_CREATORS_AMOUNT);
          setCreatorExamples(CREATOR_EXAMPLES_FALLBACK);
        }
      } finally {
        if (isMounted) {
          setLoadingCreatorExamples(false);
        }
      }
    };

    void resolveCreatorsAmount();

    return () => {
      isMounted = false;
    };
  }, [cachedCollabs, leadSubmissionId, storeAddress]);

  const businessName =
    queryParams.businessName || draft?.meta?.businessName || "Your Campaign";
  const selectedBusinessType = draft?.createStep?.businessType;
  const businessTypeLabel = selectedBusinessType
    ? BUSINESS_TYPE_LABELS[selectedBusinessType]
    : "Restaurant";
  const businessTypeEmoji = selectedBusinessType
    ? BUSINESS_TYPE_EMOJIS[selectedBusinessType]
    : "🍽️";
  const locationLabel = resolveLocationLabel(draft?.createStep?.storeAddress);
  const platformLabel = draft?.designStep?.platform
    ? PLATFORM_LABELS[draft.designStep.platform]
    : PLATFORM_LABELS.instagram;
  const averageApplications = useMemo(() => {
    return Math.floor(Math.random() * 31) + 20;
  }, []);

  const handleLaunchPlan = async (planId: SelfServeCampaignActivatePlanId) => {
    if (!leadSubmissionId) {
      setLaunchError(
        "Lead submission not found. Please restart from the first step.",
      );
      return;
    }

    try {
      setLaunchError(null);
      setLaunchingPlanId(planId);
      saveSelfServeCampaignActivateStepDraft({ selectedPlanId: planId });

      const response = await generateLeadSubmissionPaymentLink(
        leadSubmissionId,
        {
          planName: PLAN_NAME_BY_ID[planId],
        },
      );
      const paymentLinkUrl = extractPaymentLinkUrl(response);

      if (!paymentLinkUrl) {
        throw new Error("Missing payment link URL.");
      }

      window.location.assign(paymentLinkUrl);
    } catch (_error) {
      setLaunchError("We could not start checkout. Please try again.");
    } finally {
      setLaunchingPlanId(null);
    }
  };

  const handleLaunchPartnerPaywall = async () => {
    if (!leadSubmissionId || !partnerExperience) {
      window.location.assign(SELF_SERVICE_WELCOME_REDIRECT_URL);
      return;
    }

    try {
      setLaunchError(null);
      setLaunchingPartnerPaywall(true);
      saveSelfServeCampaignActivateStepDraft({
        selectedPlanId: PLAN_ID_BY_NAME[partnerExperience.selfCampaignPlanName],
      });

      const response = await generateLeadSubmissionPaymentLink(
        leadSubmissionId,
        {
          planName: partnerExperience.selfCampaignPlanName,
        },
      );
      const paymentLinkUrl = extractPaymentLinkUrl(response);

      if (!paymentLinkUrl) {
        throw new Error("Missing payment link URL.");
      }

      window.location.assign(paymentLinkUrl);
    } catch (_error) {
      setLaunchError("We could not start checkout. Please try again.");
    } finally {
      setLaunchingPartnerPaywall(false);
    }
  };

  return (
    <SelfCampaignPageLayout
      rootClassName="self-campaign-activate"
      variant="activate"
    >
      <SelfCampaignProgress
        title="Activate your campaign"
        activeStep={4}
        variant="activate"
        subtitle="Start receiving creator applications in your area."
        caption="Choose your launch package and publish your campaign to local creators."
      />

      {shouldShowPartnerPaywall && partnerExperience ? (
        <>
          {launchError ? (
            <p className="self-campaign-activate__status-message">
              {launchError}
            </p>
          ) : null}
          <section className="self-campaign-activate__partner-paywall-shell">
            <div className="self-campaign-activate__partner-paywall-modal">
              <PartnerPaywallContent
                variant={partnerExperience.paywallVariant}
                loading={launchingPartnerPaywall}
                onCtaClick={() => {
                  void handleLaunchPartnerPaywall();
                }}
                ctaLabel={partnerExperience.paywallCtaLabel}
                showCloseButton={false}
                desktopTrustedByText="Trusted by 1000+ local restaurants"
                mobileTrustedByText="Trusted by 1000+ local businesses"
                showTerms
              />
            </div>
          </section>
        </>
      ) : (
        <>
          <section className="self-campaign-activate__trust-banner">
            <p>
              <span>✨</span>
              <span>
                {creatorsAmount} creators in Los Angeles ready to collaborate
              </span>
            </p>
            <p>
              <span>🔥</span>
              <span>
                {businessTypeLabel} businesses receive ~{averageApplications}{" "}
                creator applications on average
              </span>
            </p>
          </section>

          <section className="self-campaign-activate__preview-card self-campaign-activate__preview-card--mobile">
            <h2>Your Campaign Preview</h2>
            <div className="self-campaign-activate__preview-body">
              <div className="self-campaign-activate__preview-logo" />
              <div>
                <p className="self-campaign-activate__preview-title">
                  {businessName}
                </p>
                <p className="self-campaign-activate__preview-meta">
                  📍 {locationLabel} <span>•</span> {businessTypeEmoji}{" "}
                  {businessTypeLabel}
                </p>
                <p className="self-campaign-activate__preview-meta">
                  📱 {platformLabel}
                </p>
              </div>
            </div>
          </section>

          <section className="self-campaign-activate__columns">
            <div className="self-campaign-activate__left-column">
              {launchError ? (
                <p className="self-campaign-activate__status-message">
                  {launchError}
                </p>
              ) : null}
              <div className="self-campaign-activate__plans">
                {ACTIVATE_PLANS.map((plan) => {
                  const isLaunching = launchingPlanId === plan.id;

                  return (
                    <article
                      key={plan.id}
                      className={`self-campaign-activate__plan-card ${
                        plan.isMostPopular
                          ? "self-campaign-activate__plan-card--most-popular"
                          : ""
                      }`}
                    >
                      {plan.isMostPopular ? (
                        <span className="self-campaign-activate__plan-chip">
                          Most Popular
                        </span>
                      ) : null}

                      <h3>{plan.name}</h3>
                      <div className="self-campaign-activate__plan-price">
                        <strong>{plan.priceLabel}</strong>
                        <span>/month</span>
                      </div>

                      <ul>
                        {plan.features.map((feature) => (
                          <li key={feature}>
                            <CheckOutlined />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        type="button"
                        className="self-campaign-activate__plan-button"
                        onClick={(event) => {
                          event.preventDefault();
                          void handleLaunchPlan(plan.id);
                        }}
                        disabled={Boolean(launchingPlanId) || !leadSubmissionId}
                      >
                        {isLaunching
                          ? "Redirecting to Checkout..."
                          : plan.ctaLabel}
                      </button>

                      <p className="self-campaign-activate__plan-note">
                        <LockOutlined />
                        <span>
                          Secure checkout • Campaign goes live immediately
                        </span>
                      </p>
                    </article>
                  );
                })}
              </div>

              <section className="self-campaign-activate__all-plans self-campaign-activate__all-plans--desktop">
                <h3>All plans include</h3>
                <ul>
                  {ALL_PLANS_INCLUDE.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>

            <aside className="self-campaign-activate__right-column">
              <section className="self-campaign-activate__preview-card self-campaign-activate__preview-card--desktop">
                <h2>Your Campaign Preview</h2>
                <div className="self-campaign-activate__preview-body">
                  <div className="self-campaign-activate__preview-logo" />
                  <div>
                    <p className="self-campaign-activate__preview-title">
                      {businessName}
                    </p>
                    <p className="self-campaign-activate__preview-meta">
                      📍 {locationLabel} <span>•</span> {businessTypeEmoji}{" "}
                      {businessTypeLabel}
                    </p>
                    <p className="self-campaign-activate__preview-meta">
                      📱 {platformLabel}
                    </p>
                  </div>
                </div>
              </section>

              <section className="self-campaign-activate__examples">
                <h3>Example Creator Content</h3>
                <div className="self-campaign-activate__examples-grid">
                  {loadingCreatorExamples ? (
                    <>
                      <article className="self-campaign-activate__example-card self-campaign-activate__example-card--loading" />
                      <article className="self-campaign-activate__example-card self-campaign-activate__example-card--loading" />
                      <article className="self-campaign-activate__example-card self-campaign-activate__example-card--loading" />
                    </>
                  ) : (
                    creatorExamples.map((example) => (
                      <article
                        key={`${example.handle}-${example.image}`}
                        className="self-campaign-activate__example-card"
                      >
                        <img src={example.image} alt={example.handle} />
                        <div className="self-campaign-activate__example-overlay" />
                        <div className="self-campaign-activate__example-footer">
                          <p>{example.handle}</p>
                          <span>{example.metric}</span>
                        </div>
                      </article>
                    ))
                  )}
                </div>
              </section>

              <section className="self-campaign-activate__testimonials">
                {loadingTestimonials ? (
                  <>
                    <article className="self-campaign-activate__testimonial-card self-campaign-activate__testimonial-card--loading" />
                    <article className="self-campaign-activate__testimonial-card self-campaign-activate__testimonial-card--loading" />
                  </>
                ) : (
                  testimonials.map((testimonial) => (
                    <article
                      key={testimonial.id}
                      className="self-campaign-activate__testimonial-card"
                    >
                      <div className="self-campaign-activate__testimonial-head">
                        {testimonial.logoUrl ? (
                          <img
                            src={testimonial.logoUrl}
                            alt={testimonial.name}
                            className="self-campaign-activate__testimonial-logo"
                          />
                        ) : (
                          <span
                            className={`self-campaign-activate__testimonial-icon self-campaign-activate__testimonial-icon--${testimonial.accent}`}
                          />
                        )}
                        <div>
                          <p>{testimonial.name}</p>
                          <small>{testimonial.roleDescription}</small>
                        </div>
                      </div>
                      <p className="self-campaign-activate__testimonial-quote">
                        {testimonial.quote}
                      </p>
                    </article>
                  ))
                )}
              </section>
            </aside>
          </section>

          <section className="self-campaign-activate__all-plans self-campaign-activate__all-plans--mobile">
            <h3>All plans include</h3>
            <ul>
              {ALL_PLANS_INCLUDE.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        </>
      )}
    </SelfCampaignPageLayout>
  );
};

export default SelfCampaignActivate;
